import { useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";

import { BenchmarkReportPdf } from "./BenchmarkReportPdf";
import { Header } from "./components/Report/Header/Header";
import { ReportContent } from "./components/Report/ReportContent";
import { SubscriptionModal } from "./components/Report/SubscriptionModal/SubscriptionModal";
import { generateAutomationName } from "./helpers/helpers";
import useGAMetaDataRequest from "./hooks/useGAMetaDataRequest/useGAMetaDataRequest";
import { useOpenModalFromUrl } from "./hooks/useOpenModalFromUrl";
import { useUserdataActivationQuery } from "./queries/useUserdataActivationQuery";
import { getBenchmarkEffect } from "./store/effects/report";
import { selectSelectedBenchmark } from "./store/selectors/entitySelectors";
import type { BenchmarkUrlParams } from "./types/paramsTypes";

const ReportWrapper = styled.div`
    padding: 24px 44px 104px;
    background: ${colorsPalettes.bluegrey[100]};

    @media (min-width: 1920px) {
        padding: 24px 120px 104px;
    }
`;

const BenchmarkReport = () => {
    const dispatch = useDispatch();

    const {
        params: { key },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();

    useGAMetaDataRequest();

    useEffect(() => {
        dispatch(getBenchmarkEffect(key));
    }, [dispatch, key]);

    const bench = useSelector(selectSelectedBenchmark);
    // selectedBenchmark from redux might have the previous value during first render
    const activationSetId = bench?.id === key ? bench?.activationSetId : undefined;
    const { data: currentActivationSet, isLoading, isFetching } = useUserdataActivationQuery(activationSetId);

    const isViewerMode = !currentActivationSet?.canEdit;
    const isGaSource = currentActivationSet?.trafficSource === "GA";

    useOpenModalFromUrl();

    return (
        <>
            <SubscriptionModal isViewerMode={isViewerMode} />

            <ReportWrapper data-automation={generateAutomationName("report", ["wrapper"])}>
                <Header
                    currentActivationSet={currentActivationSet}
                    isLoading={isLoading || isFetching}
                    isViewerMode={isViewerMode}
                />

                <ReportContent
                    activationId={currentActivationSet?.id}
                    isGaSource={isGaSource}
                />
            </ReportWrapper>
        </>
    );
};

const BenchmarkReportContainer = () => {
    const {
        params: { pdf },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();

    if (pdf) {
        return <BenchmarkReportPdf />;
    }

    return <BenchmarkReport />;
};

export default BenchmarkReportContainer;
